import { useEffect, useRef, useState } from "react";
import { Coins } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ms, fmtClock, phaseOf, useClock } from "@/lib/goldrain";
import { playAlarm, playCoin, playFanfare, unlockAudio, confetti } from "@/lib/celebrate";
import { GoldRainResults } from "./GoldRainResults";

const COMBO_GAP = 1500;

const rnd = (i, k) => { const x = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453; return x - Math.floor(x); };

export function GoldRainSpacer({ ev, offset }) {
  const t = useClock(offset, 1000, !!ev);
  return ["countdown", "raining"].includes(phaseOf(ev, t)) ? <div className="h-10" /> : null;
}

export function GoldRainBanner({ ev, offset, myCoins, blocked }) {
  const t = useClock(offset, 500, !!ev);
  const phase = phaseOf(ev, t);
  if (phase !== "countdown" && phase !== "raining") return null;
  const left = Math.ceil(((phase === "countdown" ? ms(ev.start_at) : ms(ev.end_at)) - t) / 1000);
  return (
    <div className="gold-banner h-10 flex items-center justify-center gap-2 px-3 text-amber-950 font-extrabold text-xs sm:text-sm uppercase tracking-wider shadow-md" data-testid="goldrain-banner">
      <Coins className="w-4 h-4 shrink-0" />
      {blocked ? <span data-testid="goldrain-banner-blocked">Goldregen – du bist für dieses Spiel gesperrt</span> : phase === "countdown"
        ? <span data-testid="goldrain-banner-text">Goldregen kommt in <span className="font-mono text-base tabular-nums" data-testid="goldrain-countdown">{fmtClock(left)}</span></span>
        : <span data-testid="goldrain-banner-text">Goldregen läuft! Noch <span className="font-mono tabular-nums" data-testid="goldrain-time-left">{fmtClock(left)}</span> · Gefangen: <span data-testid="goldrain-my-coins">{myCoins}</span></span>}
      <Coins className="w-4 h-4 shrink-0" />
    </div>
  );
}

function Coin({ i, ev, t, onCatch }) {
  const spawn = ms(ev.start_at) + (i * ev.duration * 1000) / ev.coins;
  const fall = 3.2 + rnd(i, 2) * 2.6;
  const size = 0.85 + rnd(i, 3) * 0.4;
  const [delay] = useState(() => -(t - spawn) / 1000);
  return (
    <button type="button" aria-label="Münze fangen" data-testid={`goldrain-coin-${i}`} className="gold-coin"
      style={{ left: `${6 + rnd(i, 1) * 88}%`, animationDuration: `${fall}s`, animationDelay: `${delay}s`, scale: String(size) }}
      onPointerDown={(e) => onCatch(i, e)}>1</button>
  );
}

function RainLayer({ ev, offset, onCatch, caught, combo }) {
  const t = useClock(offset, 150, true);
  const pops = useRef([]);
  const [, force] = useState(0);
  const visible = [];
  for (let i = 0; i < ev.coins; i++) {
    const spawn = ms(ev.start_at) + (i * ev.duration * 1000) / ev.coins;
    if (spawn > t) break;
    if (!caught.has(i) && t - spawn < (3.2 + rnd(i, 2) * 2.6) * 1000) visible.push(i);
  }
  const handle = (i, e) => {
    pops.current = [...pops.current.slice(-12), { id: `${i}-${Date.now()}`, x: e.clientX, y: e.clientY }];
    force((n) => n + 1);
    onCatch(i);
  };
  return (
    <div className="fixed inset-0 z-[95] pointer-events-none overflow-hidden" data-testid="goldrain-overlay">
      {visible.map((i) => <Coin key={i} i={i} ev={ev} t={t} onCatch={handle} />)}
      {pops.current.map((p) => <span key={p.id} className="gold-pop" style={{ left: p.x, top: p.y }}>+1</span>)}
      {combo.n >= 3 && t - combo.last < COMBO_GAP && (
        <div className="absolute top-20 lg:top-24 left-1/2 -translate-x-1/2" data-testid="goldrain-combo">
          <span key={combo.n} className="combo-chip inline-flex items-center gap-1.5 px-4 h-10 rounded-full font-extrabold text-[#4a2a08] tabular-nums whitespace-nowrap">
            🔥 Combo x{combo.n} <span className="text-xs font-bold opacity-80">· {combo.n % 10 === 0 ? "Bonus!" : `noch ${10 - (combo.n % 10)} bis Bonus`}</span>
          </span>
        </div>
      )}
      {combo.burst && t - combo.burst.at < 1600 && (
        <div key={combo.burst.at} className="absolute inset-0 flex items-center justify-center" data-testid="goldrain-combo-burst">
          <div className="combo-burst text-center">
            <p className="font-display text-5xl sm:text-6xl font-black combo-gold-text">COMBO x{combo.burst.n}!</p>
            <p className="mt-2 inline-flex items-center gap-2 px-4 h-10 rounded-full combo-chip font-extrabold text-[#4a2a08]"><Coins className="w-5 h-5" /> +1 Bonus-Münze</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function GoldRain({ ev, setEv, offset, reload, blocked }) {
  const { setUser } = useAuth();
  const t = useClock(offset, 1000, !!ev);
  const phase = phaseOf(ev, t);
  const [caught, setCaught] = useState(new Set());
  const sent = useRef({ pending: [], busy: false, timer: null, bonus: 0 });
  const [combo, setCombo] = useState({ n: 0, last: 0, burst: null });
  const id = ev?.id;

  useEffect(() => {
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);
  useEffect(() => { setCaught(new Set(ev?.my_caught || [])); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (blocked || !id || phase !== "countdown" || localStorage.getItem(`gr_alarm_${id}`)) return;
    localStorage.setItem(`gr_alarm_${id}`, "1");
    playAlarm(30);
    navigator.vibrate?.(Array.from({ length: 60 }, (_, k) => (k % 2 ? 150 : 350)));
  }, [id, phase, blocked]);
  useEffect(() => {
    if (blocked || phase !== "raining" || localStorage.getItem(`gr_start_${id}`)) return;
    localStorage.setItem(`gr_start_${id}`, "1");
    playFanfare();
    confetti({ x: innerWidth / 2, y: 80, count: 80, colors: ["#fbbf24", "#f59e0b", "#fde68a", "#fff7ed"] });
  }, [id, phase, blocked]);
  useEffect(() => {
    if (phase !== "done" || ev?.top) return;
    const tm = setTimeout(reload, 1500);
    return () => clearTimeout(tm);
  }, [phase, ev?.top, reload]);

  const onCatch = (i) => {
    if (caught.has(i) || sent.current.pending.includes(i)) return;
    setCaught((s) => new Set(s).add(i));
    playCoin(1 + ((ev.my_coins || 0) % 12));
    const at = Date.now() + offset, c = combo;
    const n = at - c.last <= COMBO_GAP ? c.n + 1 : 1;
    const bonus = n % 10 === 0;
    setCombo({ n, last: at, burst: bonus ? { n, at } : c.burst });
    if (bonus) { sent.current.bonus += 1; playCoin(14); setTimeout(() => playCoin(16), 120); navigator.vibrate?.([30, 40, 60]); }
    setEv((e) => ({ ...e, my_coins: (e.my_coins || 0) + 1 }));
    sent.current.pending.push(i);
    if (!sent.current.timer) sent.current.timer = setTimeout(flush, 700);
  };

  // batch catches: one request per ~0.7s instead of one per coin (prevents request pile-up & freezing)
  const flush = () => {
    const s = sent.current;
    s.timer = null;
    if (s.busy || (!s.pending.length && !s.bonus)) return;
    const coins = s.pending.splice(0, 60), bonus = s.bonus;
    s.bonus = 0;
    s.busy = true;
    let retry = 300;
    api.post(`/goldrain/${id}/catch-batch`, { coins, bonus }).then(({ data }) => {
      s.fails = 0;
      setUser((u) => (u ? { ...u, points: data.points } : u));
      // server is the source of truth: counter = credited coins + still-unsent coins
      setEv((e) => (e && e.id === id ? { ...e, my_coins: data.my_coins + s.pending.length } : e));
      if (data.my_caught) setCaught(new Set([...data.my_caught, ...s.pending]));
    }).catch((err) => {
      const st = err?.response?.status;
      if (st && st < 500 && st !== 429) return;
      // network/server error: re-queue so no caught coin is lost
      s.fails = (s.fails || 0) + 1;
      if (s.fails <= 8) { s.pending.unshift(...coins); s.bonus += bonus; retry = 1000 * s.fails; }
    }).finally(() => {
      s.busy = false;
      if ((s.pending.length || s.bonus) && !s.timer) s.timer = setTimeout(flush, retry);
    });
  };
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") { clearTimeout(sent.current.timer); flush(); } };
    document.addEventListener("visibilitychange", onHide);
    return () => { document.removeEventListener("visibilitychange", onHide); clearTimeout(sent.current.timer); if (sent.current.pending.length) flush(); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (blocked) return null;
  if (phase === "raining") return <RainLayer ev={ev} offset={offset} onCatch={onCatch} caught={caught} combo={combo} />;
  if (phase === "done" && ev.top && !localStorage.getItem(`gr_seen_${id}`)) {
    return <GoldRainResults ev={ev} onClose={() => { localStorage.setItem(`gr_seen_${id}`, "1"); setEv({ ...ev }); }} />;
  }
  return null;
}
